import { OverlayRef } from '@angular/cdk/overlay';
import { Location } from '@angular/common';
import { ComponentRef, computed, DestroyRef, effect, inject, Injectable, signal, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { nonNullable } from '@rma/generic/rxjs/operators/non-nullable';
import type { RmaOverlayService } from '@rma/generic/ui/overlay';
import { LazyInject } from '@rma/generic/util/lazy-inject';
import { PublicProfileContextService, PublicUserContextService, UserType } from '@rma/public/accounts/user';
import { TrackPropertyService } from '@rma/public/campaigns/private/api-track-campaign'; // need to move to public
import { ShortlistService } from '@rma/public/compare-agent/private-api/da-compare-agent'; // need to move to public
import { PlatformService } from '@rma/public/generic/util/environment';
import { FeatureFlags, FeatureFlagService } from '@rma/public/generic/util/feature-flags';
import { ComparePanelAgent } from '@rma/public/site/private/ui-compare-agent';
import { filter, firstValueFrom, map, Observable, of, switchMap, withLatestFrom } from 'rxjs';

export const TRANSLATION_SCOPE = 'siteHeader';

@Injectable()
export class SiteHeaderFacade {
  private readonly featureService = inject(FeatureFlagService);
  private readonly location = inject(Location);
  private readonly userService = inject(PublicUserContextService);
  private readonly profile = inject(PublicProfileContextService);
  private readonly lazyInjector = inject(LazyInject);
  private readonly vcr = inject(ViewContainerRef);
  private readonly trackPropertyService = inject(TrackPropertyService);
  private readonly shortlistService = inject(ShortlistService);
  private readonly platformService = inject(PlatformService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly url = signal(this.location.path());

  private readonly state = computed(() => ({
    url: this.url(),
    sideNavVisible: signal(false),
  }));

  private readonly baseUrl = toSignal(this.featureService.getLiveFeature$<string>(FeatureFlags.MarketingBaseUrl, ''));
  private readonly renownedUrl = toSignal(this.featureService.getLiveFeature$<string>(FeatureFlags.RenownedBaseUrl, ''));
  private readonly brokerageLink = toSignal(this.featureService.getLiveFeature$(FeatureFlags.HomePageBrokerageLink, false));

  private readonly shortlistAgents = this.shortlistService.resolveAgents();

  public readonly shortlistCount = this.shortlistService.shortlistCount;

  public readonly compareAgents = computed<ComparePanelAgent[]>(() =>
    [...this.shortlistAgents.agents()].reverse().map((agent) => ({
      agentCode: agent.agentCode,
      agentName: agent.agentName,
      avatarImage: agent.theme?.avatarImage,
    })),
  );

  public readonly compareAgentAdded$ = this.shortlistService.agentAdded$;
  public readonly trackPropertyCount$ = this.trackPropertyService.trackPropertyCount$;
  public readonly userType = toSignal(
    this.userService.currentUser$.pipe(
      nonNullable(),
      switchMap((user): Observable<UserType> => {
        if (user.userType === 'Consumer') {
          return of('Consumer');
        }

        // Agent vs Agency is a profile fact (anything non-individual is an agency), resolved from the profile context.
        return this.profile.isIndividual$.pipe(map((isIndividual): UserType => (isIndividual ? 'Agent' : 'Agency')));
      }),
    ),
  );

  public readonly isAuthenticated$ = this.userService.isAuthenticated$.pipe(
    filter(() => this.platformService.isPlatformBrowser || this.platformService.isPlatformBrowserBot),
  );

  public readonly landerUrl$ = toObservable(this.url).pipe(
    withLatestFrom(this.isAuthenticated$),
    map(([url, isAuthenticated]) => url === '/' && !isAuthenticated),
    switchMap((showLander) => (showLander ? this.featureService.getFeature$(FeatureFlags.AgentLanderUrl, '') : of(''))),
  );

  public readonly story = computed(() => `${this.renownedUrl()}about-us`);
  public readonly reviews = computed(() => `${this.baseUrl()}how-reviews-work`);
  public readonly agents = computed(() => `${this.renownedUrl()}agent-solutions`);
  public readonly group = computed(() => `${this.renownedUrl()}${this.brokerageLink() ? 'brokerage-solutions' : 'agency-solutions'}`);

  private overlayService?: RmaOverlayService;
  private sideNavRef?: OverlayRef;

  constructor() {
    this.location.onUrlChange((url) => this.url.set(url));

    // visibility of sidenav
    effect(() => this.getSideNavComponent(this.state().sideNavVisible()));
  }

  public toggleSideNav() {
    this.state().sideNavVisible.update((visible) => !visible);
  }

  public removeCompareAgent(agentCode: string): void {
    this.shortlistService.remove(agentCode).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private async getOverlayService() {
    if (!this.overlayService) {
      const overlayService = await this.lazyInjector.get<RmaOverlayService>(() =>
        import('@rma/generic/ui/overlay').then((m) => m.RmaOverlayService),
      );

      this.overlayService = overlayService;
    }

    return this.overlayService;
  }

  private async getSideNavComponent(show: boolean) {
    if (show) {
      const overlayService = await this.getOverlayService();

      const { SiteHeaderSidenavComponent } = await import('@rma/public/site/private/ui-side-nav');

      const { overlayRef, contentRef } = overlayService.open(this.vcr, SiteHeaderSidenavComponent, {
        hasBackdrop: true,
        disposeOnNavigation: true,
        scrollStrategy: overlayService.overlay.scrollStrategies.block(),
        positionStrategy: overlayService.overlay.position().global().right(),
      });

      if (contentRef instanceof ComponentRef) {
        contentRef.setInput('scope', TRANSLATION_SCOPE);
        contentRef.setInput('story', this.story());
        contentRef.setInput('reviews', this.reviews());
        contentRef.setInput('agents', this.agents());
        contentRef.setInput('group', this.group());

        contentRef.instance.close.subscribe(() => {
          overlayRef.detach();
          this.state().sideNavVisible.set(false);
        });
      }

      this.sideNavRef = overlayRef;

      await firstValueFrom(overlayRef.backdropClick()).then(() => {
        overlayRef.detach();
        this.state().sideNavVisible.set(false);
      });
    } else {
      this.sideNavRef?.detach();
    }
  }
}
